import { inject, Injectable } from '@angular/core';

import { NavigateType } from '@blaze-shared/api-contracts-domain';
import { AuthService } from '@blaze-shared/auth-data-access';
import { PageContext } from '@blaze-shared/projects-domain';

import { EnginePageNavigation } from '../../../engine-ports/public-api';

export interface NavigateRequest {
  isInEditor: boolean;
  navigateType: NavigateType;
  openInNewTab: boolean;
  pageContext: PageContext | undefined;
  target: string;
  targetProjectId?: string;
  urlQueryParams?: Record<string, string>;
}

@Injectable({
  providedIn: 'root',
})
export class NavigateActionService {
  private readonly authService = inject(AuthService);
  private readonly enginePageNavigation = inject(EnginePageNavigation);

  /**
   * Navigates to appropriate page (+ others) based on the type of the navigation node.
   */
  navigate({
    isInEditor,
    navigateType,
    openInNewTab,
    pageContext,
    target,
    targetProjectId,
    urlQueryParams,
  }: NavigateRequest): void {
    switch (navigateType) {
      case 'Logout':
        this.handleLogout(isInEditor);
        break;
      case 'Page':
        this.enginePageNavigation.navigateToPage({
          targetPageId: target,
          pageContext,
          openInNewTab,
          urlQueryParams,
        });
        break;
      case 'Project':
        this.enginePageNavigation.navigateToPage({
          openInNewTab,
          pageContext,
          targetPageId: target,
          targetProjectId,
          urlQueryParams,
        });
        break;
      case 'SendEmail':
        this.handleSendEmail(target);
        break;
      case 'Website':
        this.handleWebsite(target, openInNewTab);
        break;
      default:
        console.log(`Unable to navigate. Unknown navigation type: ${navigateType}`);
    }
  }

  private handleLogout(isInEditor: boolean): void {
    if (isInEditor) {
      console.log('Log out prevented in editing mode.');
      return;
    }

    this.authService.signOut(true);
  }

  private handleSendEmail(target: string | undefined): void {
    // Open mailto: link
    const a = document.createElement('a');
    a.setAttribute('target', `top`);
    a.setAttribute('href', `mailto:${target}`);
    a.click();
  }

  private handleWebsite(target: string | undefined, openInNewTab: boolean): void {
    console.log(`Opening new browser window: ${target}`);

    const openTarget = openInNewTab ? '_blank' : '_self';
    window.open(target, openTarget, 'noreferrer noopener');
  }
}
